/**
 * The WebSocket wire protocol, shared by client and server so the two can never drift.
 *
 * The server is a dumb relay plus presence: it forwards position frames to the rest of the room
 * and announces who is in it. All geometry happens on the client.
 *
 * The protocol talks about a **room** rather than a pair. A two-person session is simply a room
 * with a capacity of two, so the hero flow is a special case of the general one rather than a
 * separate code path that can rot.
 */

import { SESSION } from './config';
import type { ParticipantId } from './participants';
import { parseTelemetrySummary, type TelemetrySummary } from './telemetry';

/**
 * What someone is doing in the room.
 *
 *   anchor  stays put; everyone else walks to them
 *   walker  the full arrow experience
 *   leader  the moving target a whole group is following (tour guide, parent, front of a hike)
 *
 * Advisory, never enforced — the product suggests a choreography, it does not police one.
 */
export type Role = 'anchor' | 'walker' | 'leader';

export type PresenceEvent = 'joined' | 'left' | 'stale';

/** A position frame. `heading` is degrees clockwise from true north, or null if unavailable. */
export interface PositionFrame {
  lat: number;
  lng: number;
  /** Reported GPS accuracy in metres (the browser's `coords.accuracy`). */
  acc: number;
  heading: number | null;
  /** Client clock, epoch milliseconds. Only ever compared against the same client's own frames. */
  ts: number;
  /**
   * Metres above the ellipsoid, when the device reports it, and null the rest of the time.
   *
   * Only ever shown when the vertical gap is big enough to be the actual problem — a ski slope,
   * a stadium tier, a terraced beach. At street level it is noise and the interface says nothing.
   */
  alt?: number | null;
  /**
   * Course over ground in degrees, when the device is moving fast enough for it to mean anything.
   *
   * Relayed because a recipient drawing a "which way are they facing" chevron wants the direction
   * someone is actually travelling, which a cheap magnetometer is much worse at than the GPS.
   */
  crs?: number | null;
}

/**
 * A rally point: one place the whole room is walking to.
 *
 * This is the answer to the failure the anchor protocol only half-solves. Two people walking at
 * each other orbit; five people walking at each other is a farce. Giving the group a *place*
 * instead of a person makes every arrow point the same way and every distance count down
 * together — and unlike a person, a place does not move unless somebody moves it.
 *
 * It lives on the session in Redis under the session's own TTL, so it evaporates with everything
 * else. It is the one destination in the product that the server does hold, and it holds it
 * because the entire point is that it is shared.
 */
export interface RallyPoint {
  lat: number;
  lng: number;
  label: string;
  /** Who put it there, so the room can see who moved it. */
  by: ParticipantId;
  /** Server clock when it was set or last moved, so "moved 2 minutes ago" is honest. */
  at: number;
  /**
   * A photo of what to look for, as a self-contained data URL.
   *
   * Shown at the handoff range, where a picture of the gate beats any amount of bearing.
   */
  photo?: string | null;
}

/** Someone in the room, as everyone else sees them. */
export interface Member {
  id: ParticipantId;
  name: string | null;
  role: Role | null;
  /** Whether their socket is attached right now. */
  present: boolean;
}

/* -------------------------------------------------------------------------- */
/* Client → server                                                            */
/* -------------------------------------------------------------------------- */

export interface ClientPositionMessage extends PositionFrame {
  type: 'pos';
}

/**
 * "I am taking this role."
 *
 * In a room of exactly two this still assigns the opposite role to the other person, because
 * the anchor protocol only makes sense in pairs. In a bigger room it sets your own role and
 * nobody else's — a group has one leader and seven followers, not four of each.
 */
export interface ClientRoleMessage {
  type: 'role';
  role: Role;
}

/**
 * Set (or clear) this participant's display name.
 *
 * Sent over the socket rather than REST because the socket is already authenticated by the
 * participant token, and the name is only ever needed once someone is actually in a session.
 */
export interface ClientNameMessage {
  type: 'name';
  name: string | null;
}

/** Reported once, when this client believes it has reached whatever it was aiming at. */
export interface ClientConvergedMessage {
  type: 'converged';
}

/**
 * One device's anonymous account of the session, sent once at the end.
 *
 * Computed entirely on the client so the server never has to derive anything from a coordinate
 * — it receives counts, durations and distances, and stores exactly those.
 */
export interface ClientTelemetryMessage {
  type: 'telemetry';
  summary: TelemetrySummary;
}

/**
 * Set, move, or clear the room's rally point.
 *
 * Anybody in the room may move it, deliberately: plans change, and the person who first dropped
 * the pin is rarely the one who spots that the meeting point is on the wrong side of the fence.
 * Everybody is told who moved it, which is the accountability a lock would have provided anyway.
 */
export interface ClientDestinationMessage {
  type: 'destination';
  destination: { lat: number; lng: number; label: string; photo?: string | null } | null;
}

/**
 * Signals: the smallest possible message, and the only messaging this product will ever have.
 *
 * Two problems, one mechanism.
 *
 * **The last ten metres.** Two people standing twelve metres apart in a crowd have a problem no
 * amount of geometry solves. `wave` buzzes the other phones hard and strobes their screens for a
 * moment: "I am waving *right now*, look up." It is the human handoff, made mutual.
 *
 * **Everything else.** Plans change, and the alternative to saying so in Beeline is switching to
 * WhatsApp mid-session, which is where sessions go to die. So: five fixed phrases, no keyboard,
 * no history, nothing stored. Anything that needs a sentence needs a different app, and that is
 * the correct outcome.
 */
export const SIGNALS = [
  { id: 'wave', label: '👋 Waving now', urgent: true },
  { id: 'omw', label: 'On my way', urgent: false },
  { id: 'here', label: "I'm here", urgent: false },
  { id: 'five', label: '5 minutes', urgent: false },
  { id: 'come', label: 'Come to me instead', urgent: false },
] as const;

export type SignalKind = (typeof SIGNALS)[number]['id'];

export const isSignal = (value: unknown): value is SignalKind =>
  typeof value === 'string' && SIGNALS.some((signal) => signal.id === value);

export const signalLabel = (kind: SignalKind): string =>
  SIGNALS.find((signal) => signal.id === kind)?.label ?? kind;

export interface ClientSignalMessage {
  type: 'signal';
  signal: SignalKind;
}

export interface ClientPingMessage {
  type: 'ping';
}

export type ClientMessage =
  | ClientPositionMessage
  | ClientRoleMessage
  | ClientNameMessage
  | ClientConvergedMessage
  | ClientDestinationMessage
  | ClientSignalMessage
  | ClientTelemetryMessage
  | ClientPingMessage;

/* -------------------------------------------------------------------------- */
/* Server → client                                                            */
/* -------------------------------------------------------------------------- */

/** First frame after the socket opens: who you are, who else is here, when this all ends. */
export interface ServerHelloMessage {
  type: 'hello';
  you: ParticipantId;
  /** Everyone in the room, including you. */
  members: Member[];
  /** Epoch milliseconds. */
  expiresAt: number;
  /** How many extensions this session has left. */
  extensionsLeft: number;
  /** How many people this room can hold. */
  capacity: number;
  /** The room's rally point, if anybody has set one. */
  rally: RallyPoint | null;
}

/** A position, relayed verbatim. `serverTs` lets the client age a fix without trusting clocks. */
export interface ServerPositionMessage extends PositionFrame {
  type: 'pos';
  from: ParticipantId;
  serverTs: number;
}

export interface ServerPresenceMessage {
  type: 'presence';
  event: PresenceEvent;
  who: ParticipantId;
  /** The member's current details. Null when they have gone for good. */
  member: Member | null;
}

/** Somebody's role changed — possibly yours, possibly not. Everyone is told about everyone. */
export interface ServerRoleMessage {
  type: 'role';
  who: ParticipantId;
  role: Role;
}

/** A member's details changed — currently only ever their name. */
export interface ServerMemberMessage {
  type: 'member';
  member: Member;
}

/** Someone believes they have arrived. Advisory — every client's own check decides too. */
export interface ServerConvergedMessage {
  type: 'converged';
  who: ParticipantId;
}

/**
 * The rally point was set, moved, or cleared.
 *
 * Sent to the whole room including whoever moved it, so every device's arrow swings from the
 * same message and nobody is walking to a point that only they can see.
 */
export interface ServerDestinationMessage {
  type: 'destination';
  destination: RallyPoint | null;
}

/** Somebody signalled. Relayed verbatim to everyone else, and never stored. */
export interface ServerSignalMessage {
  type: 'signal';
  signal: SignalKind;
  from: ParticipantId;
  /** Their name at the time, so the receiving screen needs no lookup to render the toast. */
  name: string | null;
}

export interface ServerExpiredMessage {
  type: 'expired';
}

export interface ServerErrorMessage {
  type: 'error';
  code: ProtocolErrorCode;
  message: string;
}

export interface ServerPongMessage {
  type: 'pong';
}

export type ServerMessage =
  | ServerHelloMessage
  | ServerPositionMessage
  | ServerPresenceMessage
  | ServerRoleMessage
  | ServerMemberMessage
  | ServerConvergedMessage
  | ServerDestinationMessage
  | ServerSignalMessage
  | ServerExpiredMessage
  | ServerErrorMessage
  | ServerPongMessage;

export type ProtocolErrorCode =
  | 'session_not_found'
  | 'session_expired'
  | 'session_full'
  | 'bad_message'
  | 'rate_limited'
  | 'internal';

/* -------------------------------------------------------------------------- */
/* Parsing                                                                    */
/* -------------------------------------------------------------------------- */

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

const isLat = (v: unknown): v is number => isFiniteNumber(v) && v >= -90 && v <= 90;
const isLng = (v: unknown): v is number => isFiniteNumber(v) && v >= -180 && v <= 180;

/** An optional numeric field: absent, explicitly null, or a real number. Anything else is a lie. */
const optionalNumber = (v: unknown): number | null => (isFiniteNumber(v) ? v : null);

export const isRole = (value: unknown): value is Role =>
  value === 'anchor' || value === 'walker' || value === 'leader';

/**
 * Validate an untrusted inbound client frame.
 *
 * Returns `null` rather than throwing: a malformed frame is an expected event on a public
 * WebSocket, not an exception.
 */
export function parseClientMessage(raw: unknown): ClientMessage | null {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!isRecord(value)) return null;

  switch (value['type']) {
    case 'pos': {
      const { lat, lng, acc, heading, ts, alt, crs } = value;
      if (!isLat(lat) || !isLng(lng)) return null;
      if (!isFiniteNumber(acc) || acc < 0) return null;
      if (!isFiniteNumber(ts) || ts <= 0) return null;
      if (heading !== null && heading !== undefined && !isFiniteNumber(heading)) return null;
      return {
        type: 'pos',
        lat,
        lng,
        acc,
        heading: optionalNumber(heading),
        ts,
        alt: optionalNumber(alt),
        crs: optionalNumber(crs),
      };
    }
    case 'role': {
      const role = value['role'];
      return isRole(role) ? { type: 'role', role } : null;
    }
    case 'name': {
      const name = value['name'];
      if (name !== null && typeof name !== 'string') return null;
      return { type: 'name', name: normalizeName(name) };
    }
    case 'telemetry': {
      const summary = parseTelemetrySummary(value['summary']);
      return summary ? { type: 'telemetry', summary } : null;
    }
    case 'destination': {
      const raw = value['destination'];
      if (raw === null || raw === undefined) return { type: 'destination', destination: null };
      if (!isRecord(raw)) return null;

      const { lat, lng } = raw;
      if (!isLat(lat) || !isLng(lng)) return null;

      return {
        type: 'destination',
        destination: {
          lat,
          lng,
          label: normalizeLabel(raw['label']),
          photo: sanitizePhoto(raw['photo']),
        },
      };
    }
    case 'signal': {
      const signal = value['signal'];
      return isSignal(signal) ? { type: 'signal', signal } : null;
    }
    case 'converged':
      return { type: 'converged' };
    case 'ping':
      return { type: 'ping' };
    default:
      return null;
  }
}

/**
 * Police a photo arriving off the wire.
 *
 * Only a `data:` image survives. A URL here would become an `<img src>` on every phone in the
 * room, which is a third-party request from the frontend — the one thing the product promises
 * never to make — and a rather effective way of collecting everybody's IP address.
 */
export function sanitizePhoto(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  if (!/^data:image\/(png|jpeg|webp|avif);base64,[A-Za-z0-9+/=]+$/.test(value)) return null;
  return value.length <= MAX_PHOTO_CHARS ? value : null;
}

/**
 * The longest a photo may be **as a string**, prefix included.
 *
 * 220 kB of image is the budget — big enough to recognise a gate at arm's length, small enough
 * to cross a festival network to seven other phones. Base64 costs four characters per three
 * bytes, and the `data:image/jpeg;base64,` prefix costs twenty-three more, so this has to be
 * the budget *plus* both overheads.
 *
 * Getting it wrong is invisible rather than loud: a photo the capture step happily produced is
 * rejected here, the rally point is stored without it, and nobody is told. So the arithmetic is
 * written out rather than rounded to something that looks tidy.
 */
export const MAX_PHOTO_BUDGET_BYTES = 220 * 1024;
export const MAX_PHOTO_CHARS = Math.ceil((MAX_PHOTO_BUDGET_BYTES * 4) / 3) + 64;

/** Best-effort parse of an inbound server frame. Unknown types are ignored by the client. */
export function parseServerMessage(raw: unknown): ServerMessage | null {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!isRecord(value) || typeof value['type'] !== 'string') return null;
  return value as unknown as ServerMessage;
}

/**
 * The opposite of a role.
 *
 * Only meaningful for the anchor protocol, which is a two-person choreography — a leader has no
 * opposite, because everyone else in the room is already the other half of it.
 */
export const oppositeRole = (role: Role): Role => (role === 'anchor' ? 'walker' : 'anchor');

/**
 * Normalise an optional display name: trimmed, length-capped, empty means "no name".
 * Applied on both sides so the client shows exactly what the server stored.
 */
export function normalizeName(name: unknown): string | null {
  if (typeof name !== 'string') return null;
  const trimmed = name.trim().slice(0, SESSION.maxNameLength);
  return trimmed.length > 0 ? trimmed : null;
}

/** The longest a rally point's label may be. It sits under a big number, so it stays short. */
export const MAX_LABEL_LENGTH = 40;

/** A rally point always has a name, because "Meeting point" beats a bare pair of coordinates. */
export function normalizeLabel(label: unknown, fallback = 'Meeting point'): string {
  if (typeof label !== 'string') return fallback;
  const trimmed = label.trim().slice(0, MAX_LABEL_LENGTH);
  return trimmed.length > 0 ? trimmed : fallback;
}
